import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExperienceIntro } from "./ExperienceIntro.js";
import { ExperienceSpotlight } from "./ExperienceSpotlight.js";
import type { ExperienceStep } from "./types.js";
import { anchorExists } from "./useAnchorRect.js";
import { useExperience } from "./useExperience.js";

type Phase = { kind: "intro" } | { kind: "steps"; index: number };

export const ExperienceRunner = ({
	experienceId,
}: {
	experienceId: string;
}) => {
	const { t } = useTranslation();
	const { experience, completed, complete } = useExperience(experienceId);
	const [phase, setPhase] = useState<Phase>({ kind: "intro" });

	// WHY after commit: the anchors are siblings in the same pass, invisible to a render-time lookup.
	const [steps, setSteps] = useState<ExperienceStep[]>();
	useEffect(() => {
		setSteps(
			experience?.steps.filter((step) => anchorExists(step.anchor)) ?? [],
		);
	}, [experience]);

	const finish = useCallback(() => {
		complete();
		setPhase({ kind: "intro" });
	}, [complete]);

	if (!experience || completed || steps === undefined || steps.length === 0) {
		return null;
	}

	if (phase.kind === "intro") {
		return (
			<ExperienceIntro
				body={t(experience.intro.body)}
				onSkip={finish}
				onStart={() => setPhase({ index: 0, kind: "steps" })}
				skipLabel={t(experience.intro.skip)}
				startLabel={t(experience.intro.start)}
				title={t(experience.intro.title)}
			/>
		);
	}

	const step = steps[phase.index];
	if (!step) {
		return null;
	}

	return (
		<ExperienceSpotlight
			onBack={
				phase.index > 0
					? () => setPhase({ index: phase.index - 1, kind: "steps" })
					: undefined
			}
			onNext={() =>
				phase.index + 1 < steps.length
					? setPhase({ index: phase.index + 1, kind: "steps" })
					: finish()
			}
			onSkip={finish}
			step={step}
			stepCount={steps.length}
			stepNumber={phase.index + 1}
		/>
	);
};
